import { Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CancerTherapyType, CancerType, User } from '../../../../../shared/models';
import { TherapyTypeTranslation } from '../../../../../shared/models/users/cancer-therapy-type-translation.model';
import { CancerTypeTranslation } from '../../../../../shared/models/users/cancer-type-translation.model';
import { getAge, getBirthday } from '../../../../../shared/utilities/users-utils';
import { UserScoreResponse } from 'ngx-esprio-shared/lib/shared/models/account/responses/user-score-response.model';
import { UserDataSelectors } from '../../../../../core/users/selectors/user-data.selectors';

@Component({
  selector: 'app-monitored-user-details',
  templateUrl: './monitored-user-details.component.html',
  styleUrls: ['./monitored-user-details.component.scss'],
})
export class MonitoredUserDetailsComponent {
  @Input() public monitoredUser: User;

  public scoreDetails$: Observable<UserScoreResponse | null>;
  private notAvailable = 'N/A';

  constructor(private translate: TranslateService, private store: Store) {
    this.scoreDetails$ = this.store
      .select(UserDataSelectors.getScoreDetails)
      .pipe(map((s) => s ?? null));
  }

  public getCurrentWeight(): string {
    return this.getCurrentWeightWithBmi();
  }

  public getWeight90DaysAgo(): string {
    return this.getWeight90DaysAgoWithBmi();
  }

  public getCurrentWeightWithBmi(): string {
    let weight: number | null = null;
    this.scoreDetails$.subscribe((s) => (weight = s?.currentWeight ?? null)).unsubscribe();

    const fallback = this.monitoredUser?.coreData?.weightSixMonthsAgoInKg;
    const effective =
      weight != null && weight > 0 ? weight : fallback && fallback > 0 ? fallback : null;

    return this.formatWeightWithBmi(effective);
  }

  public getWeight90DaysAgoWithBmi(): string {
    let val: number | null = null;
    this.scoreDetails$.subscribe((s) => (val = s?.weight90DaysAgo ?? null)).unsubscribe();
    const effective = val != null && val > 0 ? val : null;
    return this.formatWeightWithBmi(effective);
  }

  private formatWeightWithBmi(weight: number | null): string {
    if (weight == null) return this.notAvailable;
    const bmi = this.calcBmiForWeight(weight);
    const bmiText = bmi != null ? ` (BMI: ${Math.round(bmi)})` : '';
    return `${weight} Kg${bmiText}`;
  }

  private calcBmiForWeight(weightInKg: number): number | null {
    const heightCm = this.monitoredUser?.coreData?.bodySizeInCm;
    if (!heightCm || heightCm <= 0) return null;
    const heightM = heightCm / 100;
    return weightInKg / (heightM * heightM);
  }

  public getWeightChangePct(): string {
    let pct: number | null = null;
    this.scoreDetails$.subscribe((s) => (pct = s?.weightChangePercentage ?? null)).unsubscribe();
    if (pct == null) return this.notAvailable;
    const sign = pct > 0 ? '+' : '';
    return `${sign}${pct}%`;
  }

  public getBirthday(): string {
    return getBirthday(this.monitoredUser?.coreData?.birthday);
  }

  public getAge(): string {
    const age = getAge(this.monitoredUser?.coreData?.birthday);
    return age > 0 ? `${age}` : this.notAvailable;
  }

  public getBodySize(bodySizeInCm: number): string {
    return bodySizeInCm > 0 ? `${bodySizeInCm} cm` : this.notAvailable;
  }

  public getCancerTypes(): string {
    const cancerMap = {
      [CancerType.Other]: this.translate.instant(CancerTypeTranslation.Other),
      [CancerType.Blood]: this.translate.instant(CancerTypeTranslation.Blood),
      [CancerType.Lymph]: this.translate.instant(CancerTypeTranslation.Lymph),
      [CancerType.Stomach]: this.translate.instant(CancerTypeTranslation.Stomach),
      [CancerType.Esphagus]: this.translate.instant(CancerTypeTranslation.Esphagus),
      [CancerType.Liver]: this.translate.instant(CancerTypeTranslation.Liver),
      [CancerType.Pancreas]: this.translate.instant(CancerTypeTranslation.Pancreas),
      [CancerType.BileDucts]: this.translate.instant(CancerTypeTranslation.BileDucts),
      [CancerType.Bowel]: this.translate.instant(CancerTypeTranslation.Bowel),
      [CancerType.Lung]: this.translate.instant(CancerTypeTranslation.Lung),
      [CancerType.Breast]: this.translate.instant(CancerTypeTranslation.Breast),
      [CancerType.Uterus]: this.translate.instant(CancerTypeTranslation.Uterus),
      [CancerType.Ovaries]: this.translate.instant(CancerTypeTranslation.Ovaries),
      [CancerType.Kidney]: this.translate.instant(CancerTypeTranslation.Kidney),
      [CancerType.Bladder]: this.translate.instant(CancerTypeTranslation.Bladder),
      [CancerType.Prostate]: this.translate.instant(CancerTypeTranslation.Prostate),
    };

    return (
      this.monitoredUser?.coreData?.cancerTypes?.map((type) => cancerMap[type]).join(', ') ||
      this.notAvailable
    );
  }

  public getCancerTherapyTypes(): string {
    const therapyMap = {
      [CancerTherapyType.Unknown]: this.translate.instant(TherapyTypeTranslation.Unknown),
      [CancerTherapyType.Other]: this.translate.instant(TherapyTypeTranslation.Other),
      [CancerTherapyType.Immunobody]: this.translate.instant(TherapyTypeTranslation.Immunobody),
      [CancerTherapyType.Antibody]: this.translate.instant(TherapyTypeTranslation.Antibody),
      [CancerTherapyType.Radiation]: this.translate.instant(TherapyTypeTranslation.Radiation),
      [CancerTherapyType.Hormone]: this.translate.instant(TherapyTypeTranslation.Hormone),
      [CancerTherapyType.Chemo]: this.translate.instant(TherapyTypeTranslation.Chemo),
      [CancerTherapyType.Surgery]: this.translate.instant(TherapyTypeTranslation.Surgery),
    };

    return (
      this.monitoredUser?.coreData?.therapyTypes?.map((type) => therapyMap[type]).join(', ') ||
      this.notAvailable
    );
  }
}
